/**
 * Manual Upload Dialog
 *
 * Fallback für blockierte Websites (403, Cloudflare).
 * Ermöglicht manuellen Upload von Produktbild + Daten.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Label,
  Textarea,
} from '@/components/v2/ui';

interface ManualUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (data: ManualUploadData) => Promise<void>;
  isLoading: boolean;
}

export interface ManualUploadData {
  title: string;
  description: string;
  price: string;
  currency: string;
  imageFile: File;
}

export function ManualUploadDialog({
  open,
  onClose,
  onUpload,
  isLoading,
}: ManualUploadDialogProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Bitte wähle eine Bilddatei (JPG, PNG, WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Bild ist zu groß (max 10MB)');
      return;
    }

    setImageFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Titel ist erforderlich');
      return;
    }

    if (!imageFile) {
      setError('Bild ist erforderlich');
      return;
    }

    try {
      await onUpload({
        title: title.trim(),
        description: description.trim(),
        price: price.trim(),
        currency,
        imageFile,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setCurrency('EUR');
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Produkt manuell hochladen</DialogTitle>
          <DialogDescription>
            Diese Website blockiert automatische Anfragen. Lade das Produktbild
            manuell hoch und gib die Produktdaten ein.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Upload */}
          <div className="space-y-2">
            <Label htmlFor="image">
              Produktbild <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-4">
              <label
                htmlFor="image"
                className="flex-1 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-6 cursor-pointer hover:border-orange-500 transition-colors"
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-h-32 rounded"
                    />
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-neutral-400" />
                      <span className="text-sm text-neutral-500">
                        Bild hochladen (JPG, PNG, WebP)
                      </span>
                    </>
                  )}
                </div>
                <input
                  id="image"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="sr-only"
                  disabled={isLoading}
                />
              </label>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Produkttitel <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Nike Sportschuhe Gr. 42"
              disabled={isLoading}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Produktbeschreibung..."
              rows={3}
              disabled={isLoading}
            />
          </div>

          {/* Price & Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price">Preis (optional)</Label>
              <Input
                id="price"
                type="text"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="19.99"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Währung</Label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900"
                disabled={isLoading}
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="CHF">CHF</option>
              </select>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || !imageFile || isLoading}
              className="bg-gradient-to-r from-orange-500 to-orange-600"
            >
              {isLoading ? (
                <>
                  <Upload className="h-4 w-4 mr-2 animate-pulse" />
                  {t('common.uploading')}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('common.upload')}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
